import axios from "axios";
import { fidexaHubApi } from "@/api/fidexaHub";
import type {
  FidexaHubSessionResponse,
  RequestFidexaHubOtpType,
  ResolveMagicLinkType,
  VerifyFidexaHubOtpType,
} from "./fidexaHubAuthType";

/**
 * Extrai a mensagem de erro no shape padrão da API (`{ message: string[] }`)
 * — mesmo helper de src/domain/auth/authService.ts, duplicado aqui de
 * propósito (namespace do FidexaHub não deve importar nada de domain/auth).
 */
const extractApiErrorMessage = (error: unknown, fallback: string): string => {
  if (axios.isAxiosError(error)) {
    const message = error.response?.data?.message;
    if (Array.isArray(message) && message.length > 0) return message[0];
    if (typeof message === "string" && message) return message;
  }
  return fallback;
};

/**
 * Why a magic link call failed, so the link page can pick the fallback:
 * - "gone": the link existed but expired or was used (410) — fall back to
 *   the browser's session or to OTP login.
 * - "invalid": the link never existed (400/404).
 * - "forbidden": the company no longer serves through FidexaHub (403).
 * - "unauthorized": the session was rejected (401) — the interceptor
 *   already ended it.
 * - "transient": no response or 5xx — the link may still be fine.
 */
export type MagicLinkFailure =
  | "gone"
  | "invalid"
  | "forbidden"
  | "unauthorized"
  | "transient";

export class MagicLinkError extends Error {
  readonly failure: MagicLinkFailure;

  constructor(message: string, failure: MagicLinkFailure) {
    super(message);
    this.failure = failure;
  }
}

const toMagicLinkError = (error: unknown, fallback: string): MagicLinkError => {
  const message = extractApiErrorMessage(error, fallback);

  if (!axios.isAxiosError(error) || !error.response) {
    return new MagicLinkError(message, "transient");
  }

  const { status } = error.response;

  if (status >= 500) return new MagicLinkError(message, "transient");
  if (status === 410) return new MagicLinkError(message, "gone");
  if (status === 401) return new MagicLinkError(message, "unauthorized");
  if (status === 403) return new MagicLinkError(message, "forbidden");
  return new MagicLinkError(message, "invalid");
};

// Backend serializa em snake_case (política global do projeto) — os
// endpoints do FidexaHub retornam session_token/expires_at.
const handleResolveMagicLink = async ({
  token,
}: ResolveMagicLinkType): Promise<FidexaHubSessionResponse> => {
  try {
    const { data } = await fidexaHubApi.post(
      "v2/fidexa-hub/magic-link/resolve",
      { token }
    );

    return {
      sessionToken: data["session_token"],
      expiresAt: data["expires_at"],
      memberId: data["member_id"],
    };
  } catch (error) {
    throw toMagicLinkError(error, "Link inválido ou expirado");
  }
};

// Expired/used link with a session already in this browser: opens the
// LOGGED-IN member's chat with the company that issued the link. The link's
// original recipient is never used, so a forwarded link can't open someone
// else's conversation.
const handleReopenMagicLinkChat = async ({
  token,
}: ResolveMagicLinkType): Promise<{ chatId: string }> => {
  try {
    const { data } = await fidexaHubApi.post(
      "v2/fidexa-hub/magic-link/reopen",
      { token }
    );

    const chatId = data["chat_id"];

    if (typeof chatId !== "string" || !chatId) {
      throw new MagicLinkError("Resposta inesperada do servidor", "transient");
    }

    return { chatId };
  } catch (error) {
    if (error instanceof MagicLinkError) throw error;
    throw toMagicLinkError(error, "Não foi possível abrir a conversa");
  }
};

// Passo 1 do login por OTP — só telefone, sem empresa (refactor
// Customer→Member: qualquer telefone pode pedir um código; se ainda não
// existir Member pra esse telefone, o backend cria na hora).
const handleRequestOtp = async ({
  phone,
}: RequestFidexaHubOtpType): Promise<void> => {
  try {
    await fidexaHubApi.post("v2/fidexa-hub/otp/request", { phone });
  } catch (error) {
    throw new Error(
      extractApiErrorMessage(error, "Não foi possível enviar o código")
    );
  }
};

// Passo 2 do login: valida o código e emite uma MemberSession nova.
const handleVerifyOtp = async ({
  phone,
  code,
}: VerifyFidexaHubOtpType): Promise<FidexaHubSessionResponse> => {
  try {
    const { data } = await fidexaHubApi.post("v2/fidexa-hub/otp/verify", {
      phone,
      code,
    });

    return {
      sessionToken: data["session_token"],
      expiresAt: data["expires_at"],
      memberId: data["member_id"],
    };
  } catch (error) {
    throw new Error(
      extractApiErrorMessage(error, "Não foi possível validar o código")
    );
  }
};

export const fidexaHubAuthService = {
  handleResolveMagicLink,
  handleReopenMagicLinkChat,
  handleRequestOtp,
  handleVerifyOtp,
};
